import { useCallback, useState } from 'react';

/**
 * A single boolean choice remembered per `scope`, for a page that draws one
 * of two views and wants to open on whichever one a viewer picked last
 * (issue #98): Jobs opens on the task board or the list depending on this.
 *
 * Same shape as `useCollapsedGroups` (issue #68), scaled down from a set of
 * keys to one flag: read once from `localStorage` in the initializer, write
 * through on every change, and fall back quietly when storage is denied - a
 * browser that refuses it still gets the toggle, just not the memory.
 *
 * The caller decides when a change here is "for real" and when it is not:
 * `set` is for a viewer acting on the toggle, not for a value parsed out of
 * the current URL, which is a visit's own choice and not a fact about the
 * viewer.
 */
function storageKey(scope: string): string {
  return `dyspatch.view.${scope}`;
}

function readStored(scope: string): boolean | null {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    return raw === null ? null : raw === 'true';
  } catch {
    return null;
  }
}

function writeStored(scope: string, value: boolean): void {
  try {
    localStorage.setItem(storageKey(scope), String(value));
  } catch {
    // as above
  }
}

export interface ViewPreference {
  value: boolean;
  set: (next: boolean) => void;
}

/**
 * `defaultValue` is what a first-time viewer, with nothing stored yet, sees.
 */
export function useViewPreference(scope: string, defaultValue: boolean): ViewPreference {
  const [value, setValue] = useState<boolean>(() => readStored(scope) ?? defaultValue);

  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      writeStored(scope, next);
    },
    [scope],
  );

  return { value, set };
}
